import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Mail, Download, RefreshCw, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminSecret, setAdminSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Data states
  const [earlyAccessData, setEarlyAccessData] = useState(null);
  const [usersData, setUsersData] = useState(null);
  const [boardsData, setBoardsData] = useState(null);

  const authenticate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/export/all`, {
        headers: { 'X-Admin-Secret': adminSecret }
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
        const data = await response.json();
        setEarlyAccessData(data.collections.early_access);
        setUsersData(data.collections.users);
        setBoardsData(data.collections.user_data);
      } else {
        setError('Invalid admin secret');
      }
    } catch (err) {
      setError('Connection error');
    }
    setLoading(false);
  };

  const refreshData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/export/all`, {
        headers: { 'X-Admin-Secret': adminSecret }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEarlyAccessData(data.collections.early_access);
        setUsersData(data.collections.users);
        setBoardsData(data.collections.user_data);
      }
    } catch (err) {
      console.error('Refresh error:', err);
    }
    setLoading(false);
  };

  const downloadBackup = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/export/download`, {
        headers: { 'X-Admin-Secret': adminSecret }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `wwi_backup_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const copyEmailList = () => {
    if (earlyAccessData?.data) {
      const emails = earlyAccessData.data.map(d => d.email).join('\n');
      navigator.clipboard.writeText(emails);
      alert('Email list copied to clipboard!');
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <main className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md"
        >
          <h1 className="text-2xl font-bold text-[#27272A] mb-2">Admin Access</h1>
          <p className="text-stone-500 mb-6">Enter your admin secret to continue</p>
          
          <form onSubmit={authenticate}>
            <div className="relative mb-4">
              <input
                type={showSecret ? 'text' : 'password'}
                value={adminSecret}
                onChange={(e) => setAdminSecret(e.target.value)}
                placeholder="Admin secret"
                className="w-full px-4 py-3 pr-12 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F58227]/30 focus:border-[#F58227]"
                data-testid="admin-secret-input"
              />
              <button
                type="button"
                onClick={() => setShowSecret(!showSecret)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                {showSecret ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            
            {error && (
              <p className="text-red-500 text-sm mb-4">{error}</p>
            )}
            
            <button
              type="submit"
              disabled={loading || !adminSecret}
              className="w-full bg-[#F58227] text-white py-3 rounded-xl font-semibold hover:bg-[#D96D19] transition-colors disabled:opacity-50"
              data-testid="admin-login-btn"
            >
              {loading ? 'Checking...' : 'Access Admin'}
            </button>
          </form>
          
          <Link
            to="/"
            className="block text-center mt-6 text-stone-500 hover:text-[#F58227] transition-colors"
          >
            ← Back to home
          </Link>
        </motion.div>
      </main>
    );
  }

  // Admin dashboard
  return (
    <main className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-stone-500 hover:text-[#F58227] transition-colors"
            >
              <ArrowLeft size={20} />
            </Link>
            <h1 className="text-xl font-bold text-[#27272A]">Admin Dashboard</h1>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={refreshData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-stone-600 hover:text-[#F58227] transition-colors"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={downloadBackup}
              className="flex items-center gap-2 px-4 py-2 bg-[#F58227] text-white rounded-lg hover:bg-[#D96D19] transition-colors"
            >
              <Download size={18} />
              Download Backup
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Beta Signups */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm p-6 border border-stone-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#E8F5E9] rounded-xl flex items-center justify-center">
                <Mail size={20} className="text-[#4CAF50]" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Beta Signups</p>
                <p className="text-2xl font-bold text-[#27272A]">{earlyAccessData?.count || 0}</p>
              </div>
            </div>
            <button
              onClick={copyEmailList}
              className="w-full py-2 text-sm text-[#F58227] hover:bg-[#FFF8F0] rounded-lg transition-colors"
            >
              Copy all emails
            </button>
          </motion.div>

          {/* Registered Users */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-sm p-6 border border-stone-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#E0F7FA] rounded-xl flex items-center justify-center">
                <Users size={20} className="text-[#0CBCBF]" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Registered Users</p>
                <p className="text-2xl font-bold text-[#27272A]">{usersData?.count || 0}</p>
              </div>
            </div>
            <p className="text-xs text-stone-400">Users with Google sign-in</p>
          </motion.div>

          {/* Active Boards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-sm p-6 border border-stone-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#FCEBDC] rounded-xl flex items-center justify-center">
                <span className="text-[#F58227] text-lg">📋</span>
              </div>
              <div>
                <p className="text-sm text-stone-500">Saved Boards</p>
                <p className="text-2xl font-bold text-[#27272A]">{boardsData?.count || 0}</p>
              </div>
            </div>
            <p className="text-xs text-stone-400">Users with cloud-synced data</p>
          </motion.div>
        </div>

        {/* Beta Signups List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-stone-100">
            <h2 className="text-lg font-semibold text-[#27272A]">Beta Signup Emails</h2>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {earlyAccessData?.data?.length > 0 ? (
              <table className="w-full">
                <thead className="bg-stone-50 sticky top-0">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-medium text-stone-500">#</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-stone-500">Email</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-stone-500">Signed Up</th>
                  </tr>
                </thead>
                <tbody>
                  {earlyAccessData.data.map((signup, index) => (
                    <tr key={signup.email || `signup-${index}`} className="border-t border-stone-50 hover:bg-stone-50">
                      <td className="px-6 py-3 text-sm text-stone-400">{index + 1}</td>
                      <td className="px-6 py-3 text-sm text-[#27272A]">{signup.email}</td>
                      <td className="px-6 py-3 text-sm text-stone-500">
                        {signup.signed_up_at ? new Date(signup.signed_up_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="px-6 py-12 text-center text-stone-400">
                No beta signups yet
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </main>
  );
}
